import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import commonPtBR from '../locales/pt-BR/common.json';
import authPtBR from '../locales/pt-BR/auth.json';

import commonEn from '../locales/en/common.json';
import authEn from '../locales/en/auth.json';

import authEs from '../locales/es/auth.json';
import commonEs from '../locales/es/common.json';
import authNl from '../locales/nl/auth.json';
import commonNl from '../locales/nl/common.json';
import authKa from '../locales/ka/auth.json';
import commonKa from '../locales/ka/common.json';
import authPtPT from '../locales/pt-PT/auth.json';
import commonPtPT from '../locales/pt-PT/common.json';

import scheduleDemoHeader from './schedule-demo-header.json';
import optimizationProjectCardTitle from './optimization-project-card-title.json';

/** Deep-merge plain objects (LP overlays on top of FULL_EN namespaces). */
function deepMerge(base, overlay) {
  if (!overlay || typeof overlay !== 'object' || Array.isArray(overlay)) {
    return overlay === undefined ? base : overlay;
  }
  const out = { ...base };
  Object.keys(overlay).forEach((key) => {
    const baseVal = base?.[key];
    const overlayVal = overlay[key];
    if (
      baseVal &&
      overlayVal &&
      typeof baseVal === 'object' &&
      typeof overlayVal === 'object' &&
      !Array.isArray(baseVal) &&
      !Array.isArray(overlayVal)
    ) {
      out[key] = deepMerge(baseVal, overlayVal);
    } else {
      out[key] = overlayVal;
    }
  });
  return out;
}

const authEsMerged = deepMerge(authEn, authEs);
const commonEsMerged = deepMerge(commonEn, commonEs);
const authNlMerged = deepMerge(authEn, authNl);
const commonNlMerged = deepMerge(commonEn, commonNl);
const authKaMerged = deepMerge(authEn, authKa);
const commonKaMerged = deepMerge(commonEn, commonKa);
const authPtPTMerged = deepMerge(authPtBR, authPtPT);
const commonPtPTMerged = deepMerge(commonPtBR, commonPtPT);

/**
 * Textos do link "Agendar demonstração" no header (todas as línguas do perfil).
 * Demais chaves do app seguem en ou pt-BR conforme o bundle base.
 */
function scheduleDemoOverlay(locale) {
  if (locale && scheduleDemoHeader[locale]) {
    return scheduleDemoHeader[locale];
  }
  if (!locale) {
    return scheduleDemoHeader.en;
  }
  const lang = locale.split('-')[0].toLowerCase();
  if (lang === 'pt') {
    return scheduleDemoHeader['pt-BR'];
  }
  if (lang === 'en') {
    return scheduleDemoHeader.en;
  }
  if (lang === 'es') {
    return scheduleDemoHeader['es-MX'];
  }
  if (lang === 'fr') {
    return scheduleDemoHeader['fr-FR'];
  }
  if (lang === 'de') {
    return scheduleDemoHeader['de-DE'];
  }
  if (lang === 'it') {
    return scheduleDemoHeader['it-IT'];
  }
  if (lang === 'pl') {
    return scheduleDemoHeader['pl-PL'];
  }
  if (lang === 'nl') {
    return scheduleDemoHeader.en;
  }
  return scheduleDemoHeader.en;
}

function mergeHeader(baseHeader, locale) {
  return { ...baseHeader, ...scheduleDemoOverlay(locale) };
}

/** Título do card de otimizações na página do projeto (por locale do perfil). */
function patchProjectOptimizationsCardTitle(projectNs, localeCode) {
  const title =
    optimizationProjectCardTitle[localeCode] ||
    optimizationProjectCardTitle[String(localeCode).split('-')[0]] ||
    optimizationProjectCardTitle.en;
  return {
    ...projectNs,
    optimizations: {
      ...projectNs.optimizations,
      projectCardTitle: title,
    },
  };
}

/** Idiomas com bundle completo em inglês + strings do agendamento no header. */
const EN_LIKE_UI_LOCALES = [
  'en-US',
  'en-GB',
  'en-CA',
  'en-GE',
  'es-CL',
  'es-CO',
  'es-PE',
  'fr-FR',
  'de-DE',
  'it-IT',
  'pl-PL',
];

const ES_LP_LOCALES = ['es-ES', 'es-MX'];

/**
 * Monta os recursos de todos os locales a partir dos namespaces de pt-BR e en.
 * Na abertura recebe só common/auth (CORE_*); depois, as traduções completas (fullBundles).
 * header/project só entram quando o namespace base existe, para não criar bundles parciais.
 */
function buildResources(FULL_PT, FULL_EN) {
  const headerFor = (base, locale) => (base ? { header: mergeHeader(base, locale) } : {});
  const projectFor = (locale) =>
    FULL_EN.project ? { project: patchProjectOptimizationsCardTitle(FULL_EN.project, locale) } : {};

  const buildLpLocaleBundle = (locale, authMerged, commonMerged, headerLocale) => ({
    ...FULL_EN,
    auth: authMerged,
    common: commonMerged,
    ...headerFor(FULL_EN.header, headerLocale || locale),
    ...projectFor(locale),
  });

  const enLikeBundle = (headerLocale, projectLocale) => ({
    ...FULL_EN,
    ...headerFor(FULL_EN.header, headerLocale),
    ...projectFor(projectLocale),
  });

  return {
    'pt-BR': { ...FULL_PT, ...headerFor(FULL_PT.header, 'pt-BR') },
    'pt-PT': {
      ...FULL_PT,
      auth: authPtPTMerged,
      common: commonPtPTMerged,
      ...headerFor(FULL_PT.header, 'pt-PT'),
    },
    pt: { ...FULL_PT, ...headerFor(FULL_PT.header, 'pt') },
    en: { ...FULL_EN, ...headerFor(FULL_EN.header, 'en') },
    ...Object.fromEntries(EN_LIKE_UI_LOCALES.map((locale) => [locale, enLikeBundle(locale, locale)])),
    ...Object.fromEntries(
      ES_LP_LOCALES.map((locale) => [
        locale,
        buildLpLocaleBundle(locale, authEsMerged, commonEsMerged, locale),
      ])
    ),
    'nl-NL': buildLpLocaleBundle('nl-NL', authNlMerged, commonNlMerged, 'nl-NL'),
    'ka-GE': buildLpLocaleBundle('ka-GE', authKaMerged, commonKaMerged, 'ka-GE'),
    // Códigos curtos (ex.: detector do navegador) com mesmo tratamento que a região principal.
    es: buildLpLocaleBundle('es', authEsMerged, commonEsMerged, 'es-MX'),
    nl: buildLpLocaleBundle('nl', authNlMerged, commonNlMerged, 'nl'),
    ka: buildLpLocaleBundle('ka', authKaMerged, commonKaMerged, 'ka-GE'),
    fr: enLikeBundle('fr-FR', 'fr'),
    de: enLikeBundle('de-DE', 'de'),
    it: enLikeBundle('it-IT', 'it'),
    pl: enLikeBundle('pl-PL', 'pl'),
  };
}

/** O que a landing precisa para pintar. O resto vem de fullBundles (chunk à parte). */
const CORE_PT = { common: commonPtBR, auth: authPtBR };
const CORE_EN = { common: commonEn, auth: authEn };

export const SUPPORTED_LOCALES = [
  'pt-BR',
  'pt-PT',
  'pt',
  'en',
  ...EN_LIKE_UI_LOCALES,
  ...ES_LP_LOCALES,
  'nl-NL',
  'ka-GE',
  'es',
  'nl',
  'fr',
  'de',
  'it',
  'pl',
  'ka',
];

export const DEFAULT_LOCALE = 'pt-BR';
export const LOCALE_COOKIE = 'gio_locale';

function getInitialLocale() {
  const params = new URLSearchParams(window.location.search);
  const urlLocale = params.get('locale');
  if (urlLocale && SUPPORTED_LOCALES.includes(urlLocale)) {
    return urlLocale;
  }

  const cookieMatch = document.cookie.match(new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=([^;]*)`));
  if (cookieMatch) {
    const cookieLocale = decodeURIComponent(cookieMatch[1]);
    if (SUPPORTED_LOCALES.includes(cookieLocale)) {
      return cookieLocale;
    }
  }

  const stored = localStorage.getItem(LOCALE_COOKIE);
  if (stored && SUPPORTED_LOCALES.includes(stored)) {
    return stored;
  }

  return null;
}

// Nos testes (Jest) tudo entra síncrono; no build o webpack descarta este require.
const i18nResources =
  process.env.NODE_ENV === 'test'
    ? buildResources(require('./fullBundles').FULL_PT, require('./fullBundles').FULL_EN)
    : buildResources(CORE_PT, CORE_EN);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: i18nResources,
    lng: getInitialLocale(),
    // Sem língua detectada, o padrão continua pt-BR. Com língua definida,
    // buraco de tradução cai no inglês — não no português.
    fallbackLng: {
      en: ['en'],
      nl: ['en'],
      es: ['en'],
      fr: ['en'],
      de: ['en'],
      it: ['en'],
      pl: ['en'],
      ka: ['en'],
      default: [DEFAULT_LOCALE, 'en'],
    },
    supportedLngs: SUPPORTED_LOCALES,
    nonExplicitSupportedLngs: true,
    defaultNS: 'common',
    ns: [
      'common',
      'auth',
      'header',
      'sidebar',
      'dashboard',
      'projects',
      'campaigns',
      'plans',
      'accounts',
      'notifications',
      'project',
      'upgradeFlow',
      'creatives',
      'audiences',
      'crmConversions',
      'linkedAccounts',
      'metaAds',
      'googleAds',
      'projectDetails',
      'projectMembers',
      'alerts',
      'chat',
      'partners',
      'searchConsole',
      'funilBuilder',
    ],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['querystring', 'cookie', 'localStorage', 'navigator'],
      lookupQuerystring: 'locale',
      lookupCookie: LOCALE_COOKIE,
      lookupLocalStorage: LOCALE_COOKIE,
      caches: ['localStorage', 'cookie'],
      cookieDomain: window.location.hostname.split('.').slice(-2).join('.'),
    },
    react: {
      useSuspense: false,
    },
  });

i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
  localStorage.setItem(LOCALE_COOKIE, lng);
  const domain = window.location.hostname.split('.').slice(-2).join('.');
  document.cookie = `${LOCALE_COOKIE}=${lng};path=/;domain=.${domain};max-age=31536000;SameSite=Lax`;
});

if (i18n.language) {
  document.documentElement.lang = i18n.language;
}

/** Landings de anúncio: abrem só com common/auth (CORE_*). */
export const LIGHT_I18N_PATHS = [
  '/register-landing',
  '/register-landing-sell',
  '/register-landing-no-trial',
  '/register-landing-form-no-trial',
  '/register-landing-offer',
  '/register-landing-offer-form',
  '/register-landing-offer-price',
];

let fullTranslationsLoad = null;

/**
 * Carrega o chunk com todas as traduções e completa os bundles de cada locale.
 * Idempotente: várias chamadas compartilham a mesma promessa.
 */
export function loadAllTranslations() {
  if (!fullTranslationsLoad) {
    fullTranslationsLoad = import('./fullBundles').then(({ FULL_PT, FULL_EN }) => {
      const resources = buildResources(FULL_PT, FULL_EN);
      // Namespaces já vêm completos: troca direta, sem deep merge (~30 locales × 25 ns).
      Object.entries(resources).forEach(([lng, bundle]) => {
        Object.entries(bundle).forEach(([ns, data]) => {
          i18n.addResourceBundle(lng, ns, data, false, true);
        });
      });
      // Um re-render só para quem já está montado (o store não notifica o React sozinho).
      i18n.emit('languageChanged', i18n.language);
    });
  }
  return fullTranslationsLoad;
}

/**
 * Na landing as traduções completas não são usadas: só carregam quando a pessoa
 * interage (toque, rolagem, tecla), sem entrar no carregamento inicial.
 */
export function loadAllTranslationsOnFirstInteraction() {
  if (typeof window === 'undefined') return;
  const events = ['pointerdown', 'touchstart', 'scroll', 'keydown'];
  const onFirst = () => {
    events.forEach((type) => window.removeEventListener(type, onFirst, true));
    loadAllTranslations().catch(() => {});
  };
  events.forEach((type) => window.addEventListener(type, onFirst, { capture: true, passive: true }));
}

export default i18n;
